import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, PawPrint } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { PaintSplat } from "./PaintSplat";
import { Magnetic } from "./Magnetic";

const SRC = "/schnauzer-swirl.mp4";
const MOBILE_SRC = "/schnauzer-swirl-mobile.mp4";
const POSTER = "/schnauzer-swirl-poster.webp";

const EASE = [0.16, 1, 0.3, 1];

export const Showreel = () => {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const endedRef = useRef(false);
  const [showContent, setShowContent] = useState(false);
  const reduceMotion = useReducedMotion();

  // Subtle scroll parallax: the film drifts + scales gently as the hero leaves.
  // Baseline scale > 1 keeps the frame fully covered while it translates.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "9%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.16]);

  // Serve the lighter film to phones (matches the media-scoped preload in index.html).
  const [src] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
      ? MOBILE_SRC
      : SRC
  );

  // Reveal the tagline + CTAs after the film finishes (with a safety fallback
  // in case autoplay is blocked / the video never reaches its end).
  useEffect(() => {
    const fallback = setTimeout(() => setShowContent(true), 12000);
    return () => clearTimeout(fallback);
  }, []);

  // Play only while on screen (perf + nudges autoplay). Never restart once ended.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (endedRef.current) return;
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  // Film plays once, then holds on its final frame while the content shows.
  const handleEnded = () => {
    endedRef.current = true;
    setShowContent(true);
  };

  return (
    <section ref={sectionRef} id="hero" className="relative pt-24 md:pt-28 pb-8 md:pb-12 px-4 sm:px-6 md:px-10 overflow-hidden">
      <PaintSplat color="teal" className="w-[380px] h-[380px] top-2 -left-16" rotate={-18} opacity={0.32} from={30} to={-30} />
      <PaintSplat color="orange" className="w-[420px] h-[420px] -bottom-12 -right-20" rotate={150} opacity={0.3} from={-30} to={30} />
      <div className="mx-auto max-w-[1280px] relative z-10">
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, scale: 0.94, y: 26 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.08 }}
          className="relative overflow-hidden rounded-3xl border border-[#242424] aspect-video md:max-h-[calc(100svh-175px)] shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
        >
          <motion.video
            ref={videoRef}
            src={src}
            poster={POSTER}
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={handleEnded}
            style={reduceMotion ? undefined : { y: videoY, scale: videoScale }}
            className="w-full h-full object-cover origin-center will-change-transform"
            data-testid="hero-video"
          />

          {/* Overlay — desktop / tablet only (never overlaps the film on phones) */}
          <div className="absolute inset-0 p-6 md:p-10 hidden sm:block">
            {/* Phase 1 — brand wordmark, bottom-left. Slides up + out once the film ends. */}
            <motion.div
              animate={{ y: showContent ? -40 : 0, opacity: showContent ? 0 : 1 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="absolute bottom-8 left-8 md:bottom-10 md:left-10 pointer-events-none"
              style={{ textShadow: "0 2px 18px rgba(0,0,0,0.65)" }}
              data-testid="hero-wordmark"
            >
              <span className="font-mono-accent text-[9px] md:text-[11px] tracking-[0.3em] uppercase text-white/90 mb-3 block">
                Creative Studio — Est. 2017
              </span>
              <div className="font-display font-semibold uppercase tracking-tighter leading-[0.82] text-[#fafafa]">
                <span className="block text-4xl md:text-5xl lg:text-6xl">
                  Sil<span style={{ marginLeft: "-0.13em" }}>ver</span>
                </span>
                <span className="block text-4xl md:text-5xl lg:text-6xl">Schnauzer</span>
                <span className="block text-xl md:text-2xl lg:text-3xl tracking-[0.14em]">
                  Studios
                </span>
              </div>
            </motion.div>

            {/* Phase 2 — small dark card slides up into the bottom-left corner */}
            <motion.div
              initial={{ y: 36, opacity: 0 }}
              animate={{ y: showContent ? 0 : 36, opacity: showContent ? 1 : 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className={`absolute bottom-5 left-8 md:bottom-6 md:left-10 w-[340px] ${showContent ? "pointer-events-auto" : "pointer-events-none"}`}
              data-testid="hero-tagline"
            >
              <div className="relative rounded-xl border border-[#ff6b6b]/25 bg-[#0a0a0a]/90 backdrop-blur-md p-4 shadow-[0_18px_50px_rgba(0,0,0,0.55),0_0_44px_-10px_rgba(255,107,107,0.45)]">
                {/* Playful paw accent */}
                <span className="absolute -top-2.5 -left-2.5 grid place-items-center h-7 w-7 rounded-full bg-[#ff6b6b] text-[#0a0a0a] shadow-[0_4px_14px_rgba(255,107,107,0.5)]">
                  <PawPrint size={14} className="paw-wag" />
                </span>
                <h1 className="font-display font-medium tracking-tight leading-[1.08] text-white text-lg">
                  A software &amp; graphic design studio.
                </h1>
                <p className="text-white/70 text-[11px] leading-relaxed mt-2">
                  Products, brands &amp; interfaces — equal parts logic and art.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Magnetic>
                  <button
                    onClick={() => scrollToId("work")}
                    className="group inline-flex items-center gap-1.5 rounded-full bg-[#ff6b6b] text-[#0a0a0a] px-4 py-2 font-mono-accent text-[10px] uppercase tracking-[0.16em] font-medium hover:text-white transition-[color,box-shadow,transform] duration-300 hover:shadow-[0_0_24px_rgba(255,107,107,0.5)] hover:-translate-y-0.5"
                    data-testid="hero-view-work-btn"
                  >
                    View Work
                    <ArrowUpRight size={13} className="group-hover:rotate-45 transition-transform duration-300" />
                  </button>
                  </Magnetic>
                  <Magnetic>
                  <button
                    onClick={() => scrollToId("contact")}
                    className="inline-flex items-center rounded-full border border-white/30 text-white px-4 py-2 font-mono-accent text-[10px] uppercase tracking-[0.16em] hover:border-[#fbbf24] hover:text-[#fbbf24] hover:bg-[#fbbf24]/10 transition-colors duration-300"
                    data-testid="hero-start-project-btn"
                  >
                    Start a Project
                  </button>
                  </Magnetic>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Mobile hero content — sits BELOW the film (never overlays it on phones) */}
        <div className="sm:hidden mt-7 px-1" data-testid="hero-mobile-content">
          <span className="font-mono-accent text-[9px] tracking-[0.3em] uppercase text-white/60 block mb-3">
            Creative Studio — Est. 2017
          </span>
          <div
            className="font-display font-semibold uppercase tracking-tighter leading-[0.84] text-[#fafafa]"
            data-testid="hero-wordmark-mobile"
          >
            <span className="block text-4xl">
              Sil<span style={{ marginLeft: "-0.13em" }}>ver</span> Schnauzer
            </span>
            <span className="block text-lg tracking-[0.14em]">Studios</span>
          </div>
          <h1 className="font-display font-medium tracking-tight leading-[1.1] text-white text-xl mt-5">
            A software &amp; graphic design studio.
          </h1>
          <p className="text-white/65 text-sm leading-relaxed mt-3">
            Products, brands &amp; interfaces — equal parts logic and art.
          </p>
          <div className="flex flex-wrap gap-3 mt-5">
            <button
              onClick={() => scrollToId("work")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#ff6b6b] text-[#0a0a0a] px-5 py-3.5 font-mono-accent text-[11px] uppercase tracking-[0.18em] font-medium transition-colors duration-300 active:scale-95"
              data-testid="hero-view-work-btn-mobile"
            >
              View Work
              <ArrowUpRight size={14} />
            </button>
            <button
              onClick={() => scrollToId("contact")}
              className="inline-flex items-center rounded-full border border-white/30 text-white px-5 py-3.5 font-mono-accent text-[11px] uppercase tracking-[0.18em] active:scale-95"
              data-testid="hero-start-project-btn-mobile"
            >
              Start a Project
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
